import { useGetAccount, useGetChatIdentityToken } from "@/queries/account-queries";
import { useOrganization } from "@/queries/organization-queries";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useEffect } from "react";

const pylonUnauthenticatedUserIdKey = "pylon-unauthenticated-user-id";

const disableChat = process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test";

export const useBootInAppChat = (delayMs = 3000) => {
    const APP_ID = "efa0bf58-5742-41dd-81e3-ffcb7cccda75";
    const PYLON_WIDGET_URL = `https://widget.usepylon.com/widget/${APP_ID}`;

    const { data: account } = useGetAccount();
    const { data: organization } = useOrganization();
    // Only fetch identity token for authenticated users
    const { data: emailHash } = useGetChatIdentityToken(!!account);

    useEffect(() => {
        if (disableChat) {
            return;
        }

        // Check if script already exists
        const findScript = () => {
            return document.querySelector(`script[src="${PYLON_WIDGET_URL}"]`);
        };
        if (findScript()) {
            return;
        }

        const timeoutId = setTimeout(() => {
            // Double-check before injecting in case another instance loaded it
            if (findScript()) {
                return;
            }

            const script = document.createElement("script");
            script.setAttribute("type", "text/javascript");
            script.setAttribute("async", "true");
            script.setAttribute("src", PYLON_WIDGET_URL);
            script.onload = () => {
                disableAnimations().catch(console.error);
            };
            script.onerror = (error) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const err = error instanceof ErrorEvent ? error.error : error;
                console.log("Not booting Pylon: failed to inject script", err);
            };
            document.head.insertBefore(script, document.head.firstChild);
        }, delayMs);

        return () => {
            clearTimeout(timeoutId);
            const script = findScript();
            if (script && document.head.contains(script)) {
                try {
                    document.head.removeChild(script);
                } catch (error) {
                    console.error("Failed to remove Pylon script", error);
                }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (disableChat) {
            return;
        }
        if (!account) {
            if (!window.localStorage.getItem(pylonUnauthenticatedUserIdKey)) {
                window.localStorage.setItem(pylonUnauthenticatedUserIdKey, crypto.randomUUID());
            }
            return;
        }
    }, [account]);

    useEffect(() => {
        if (disableChat) {
            return;
        }
        // Setting window.pylon to undefined causes pylon the hide the chat bubble
        let email = account?.email;
        if (!email) {
            email = `unauthenticated-${window.localStorage.getItem(pylonUnauthenticatedUserIdKey)}@example.com`;
        }

        window.pylon = {
            chat_settings: {
                app_id: APP_ID,
                email: email,
                name: account?.name ?? "Unauthenticated",
                avatar_url: account?.avatarUrl,
                account_external_id: account?.id,
                // Identity verification hash for authenticated users only
                // See: https://docs.usepylon.com/pylon-docs/chat-widget/identity-verification
                ...(account && emailHash ? { email_hash: emailHash } : {}),
            },
        };
        if (window.Pylon) {
            // Fields need to be first configured in Pylon
            const customFields: Record<string, string> = {
                product: "Flex",
            };

            // Add organization tier if available (including UNSPECIFIED which is 0)
            if (organization?.tier !== undefined) {
                // Convert enum to string representation
                const tierName =
                    Object.entries(OrganizationTier).find(([, value]) => value === organization.tier)?.[0] ||
                    "UNSPECIFIED";
                customFields.organization_tier = tierName;
            }

            window.Pylon("setNewIssueCustomFields", customFields);
        }
    }, [account, organization, emailHash]);
};

/**
 * Hides the Pylon chat bubble while the calling component is mounted,
 * then restores it on unmount. Use on pages where the bubble overlaps
 * interactive controls (e.g. pagination).
 */
export const useHidePylonChat = () => {
    useEffect(() => {
        if (disableChat) {
            return;
        }
        if (window.Pylon) {
            window.Pylon("hide");
        }
        return () => {
            if (window.Pylon) {
                window.Pylon("show");
            }
        };
    }, []);
};

export const useNewInAppChatMessage = () => {
    return (message: string) => {
        if (disableChat) {
            return;
        }
        if (window.Pylon) {
            window.Pylon("showNewMessage", message);
        }
    };
};

export const useResetPylon = () => {
    return () => {
        if (disableChat) {
            return;
        }
        window.pylon = undefined;
        window.localStorage.removeItem(pylonUnauthenticatedUserIdKey);
    };
};

const disableAnimations = async () => {
    try {
        const findAnimations = () => {
            const chatWindow = document.getElementsByClassName("PylonChat-chatWindow")[0];
            if (chatWindow && chatWindow.tagName === "IFRAME") {
                const iframeDoc = (chatWindow as HTMLIFrameElement).contentDocument;
                return iframeDoc?.getElementsByClassName("PylonChat-backgroundBlob--container")[0];
            }
        };

        for (let i = 0; i < 10; i++) {
            const animations = findAnimations();
            if (animations) {
                animations.remove();
                console.log("Animations removed");
                return;
            }
            await new Promise((resolve) => setTimeout(resolve, 1000));
        }
    } catch (error) {
        console.error("Failed to disable animations", error);
    }
};
